import {useState, useCallback, useMemo} from 'react';
import {Select} from 'antd';
import {useQuery} from '@apollo/client';
import {GET_ALL_USERS_BY_NAME} from 'graphql/queries/campaign';
import {GET_ALL_REFERRAL_PARTNERS} from 'pages/referralPartners/resolvers';
import {debounce, sanitizeInput} from 'utils/helper';
import {toast} from 'react-toastify';

/**
 * Picks the owner of a promo code, filtered to the population the selected
 * owner type implies.
 *
 * NOTE: `getAllUsers` has no sales-agent role filter today, so AGENT falls
 * back to the admin population. Swap `filters.user` for a role filter once
 * users-service exposes one.
 */
export const CouponOwnerPicker = ({ownerType, value, onChange, disabled}) => {
  const [search, setSearch] = useState('');
  const isPartner = ownerType === 'REFERRAL_PARTNER';

  const {data: partnersData, loading: loadingPartners} = useQuery(
    GET_ALL_REFERRAL_PARTNERS,
    {
      variables: {
        page: 1,
        limit: 50,
        ...(search ? {filters: {search}} : {}),
      },
      skip: !isPartner,
      errorPolicy: 'all',
      onError: error => toast.error(error.message),
    },
  );

  const {data: usersData, loading: loadingUsers} = useQuery(
    GET_ALL_USERS_BY_NAME,
    {
      variables: {
        filters: {
          pagination: {limit: 50},
          user: {admin: true},
          ...(search ? {search} : {}),
        },
      },
      skip: isPartner || ownerType !== 'AGENT',
      errorPolicy: 'all',
      onError: error => toast.error(error.message),
    },
  );

  const options = useMemo(() => {
    const source = isPartner
      ? partnersData?.adminGetReferralPartners?.partners || []
      : usersData?.getAllUsers?.users || [];
    return source.map(person => ({
      value: person.id,
      label:
        `${person.firstName || ''} ${person.lastName || ''}`.trim() ||
        person.email ||
        person.id,
      email: person.email,
    }));
  }, [isPartner, partnersData, usersData]);

  const handleSearch = useCallback(
    debounce(term => setSearch(term ? sanitizeInput(term) : '')),
    [],
  );

  return (
    <Select
      showSearch
      allowClear
      disabled={disabled}
      value={value}
      onChange={onChange}
      loading={loadingPartners || loadingUsers}
      onSearch={handleSearch}
      filterOption={false}
      notFoundContent={
        loadingPartners || loadingUsers ? 'Searching…' : 'No matches'
      }
      placeholder={
        isPartner ? 'Search referral partners' : 'Search sales agents'
      }>
      {options.map(option => (
        <Select.Option key={option.value} value={option.value}>
          {option.label}
          {option.email ? ` — ${option.email}` : ''}
        </Select.Option>
      ))}
    </Select>
  );
};
